import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '@store';
import {userAPI} from '@api/methods';
import {IDLE, PENDING} from '@constants/loadingState';
import {IRole, IUser} from '@types';

interface UsersState {
  loading: string;
  inviteUserLoading: string;
  deleteUsersLoading: string;
  sendReminderLoading: string;
  token: string;
  refresh_token: string;
  users: IUser[];
  system_roles: IRole[];
  roles: IRole[];
}

export const authByEmail = createAsyncThunk<
  {access_token: string; refresh_token: string},
  any,
  {state: RootState}
>('user/auth/email', async ({email, password}: {email: string; password: string}) => {
  return await userAPI.authByEmail(email, password);
});

export const recoveryPassword = createAsyncThunk<any, any, {state: RootState}>(
  'user/recovery/password',
  async ({email}: {email: string}) => {
    return await userAPI.recoveryPassword(email);
  },
);

export const recoveryPasswordSet = createAsyncThunk<
  any,
  {email: string; code: string; password: string},
  {state: RootState}
>('user/recovery/password/set', async ({email, code, password}) => {
  return await userAPI.recoveryPasswordSet(email, code, password);
});

export const getUsers = createAsyncThunk<any, any, {state: RootState}>(
  'user/get/all',
  async () => {
    return await userAPI.getUsers();
  },
);

export const inviteUser = createAsyncThunk<string, any, {state: RootState}>(
  'user/invite/new',
  async ({
    username,
    email,
    role_id,
  }: {
    email: string;
    username: string;
    role_id: string;
  }) => {
    return await userAPI.inviteUser(email, username, role_id);
  },
);

export const acceptInvite = createAsyncThunk<
  string,
  {email: string; code: string; password: string},
  {state: RootState}
>('user/invite/accept', async ({email, code, password}) => {
  return await userAPI.acceptInvite(email, code, password);
});

export const sendReminder = createAsyncThunk<string, any, {state: RootState}>(
  'user/invite/reminder',
  async ({emails}: {emails: string[]}) => {
    return await userAPI.sendReminder(emails);
  },
);

export const updateUser = createAsyncThunk<string, any, {state: RootState}>(
  'user/update',
  async ({
    id,
    username,
    email,
    role_id,
  }: {
    id: number;
    email: string;
    username: string;
    role_id: string;
  }) => {
    return await userAPI.updateUser(id, email, username, role_id);
  },
);

export const deleteUsers = createAsyncThunk<string, number[], {state: RootState}>(
  'user/delete',
  async users_ids => {
    return await userAPI.deleteUsers(users_ids);
  },
);

const initialState: UsersState = {
  loading: IDLE,
  inviteUserLoading: IDLE,
  deleteUsersLoading: IDLE,
  sendReminderLoading: IDLE,
  token: '',
  refresh_token: '',
  users: [],
  system_roles: [
    {
      id: 1,
      name: 'Тех. Администратор',
    },
    {
      id: 2,
      name: 'Администратор Пространства',
    },
    {
      id: 3,
      name: 'Пользователь',
    },
  ],
  roles: [
    {
      id: 1,
      name: 'Редактор',
    },
    {
      id: 2,
      name: 'Наблюдатель',
    },
    {
      id: 3,
      name: 'Администратор',
    },
  ],
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setTokens(
      state,
      action: PayloadAction<{access_token: string; refresh_token: string}>,
    ) {
      if (action.payload) {
        state.token = action.payload.access_token;
        state.token = action.payload.refresh_token;
      }
    },
    logout(state) {
      state.token = '';
      state.refresh_token = '';
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    },
  },
  extraReducers: builder => {
    builder.addCase(authByEmail.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(authByEmail.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(authByEmail.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.token = payload.access_token;
      state.refresh_token = payload.refresh_token;
      localStorage.setItem('authToken', payload.access_token);
      localStorage.setItem('refreshToken', payload.refresh_token);
    });
    builder.addCase(recoveryPassword.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(recoveryPassword.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(recoveryPassword.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      console.log(payload);
    });
    builder.addCase(recoveryPasswordSet.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(recoveryPasswordSet.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(recoveryPasswordSet.fulfilled, state => {
      state.loading = IDLE;
    });
    builder.addCase(getUsers.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(getUsers.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(getUsers.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.users = payload;
    });
    builder.addCase(inviteUser.pending, state => {
      state.inviteUserLoading = PENDING;
    });
    builder.addCase(inviteUser.rejected, state => {
      state.inviteUserLoading = IDLE;
    });
    builder.addCase(inviteUser.fulfilled, (state, {payload}) => {
      state.inviteUserLoading = IDLE;
      console.log('payload', payload);
    });
    builder.addCase(acceptInvite.pending, state => {
      state.inviteUserLoading = PENDING;
    });
    builder.addCase(acceptInvite.rejected, state => {
      state.inviteUserLoading = IDLE;
    });
    builder.addCase(acceptInvite.fulfilled, state => {
      state.inviteUserLoading = IDLE;
    });
    builder.addCase(updateUser.pending, state => {
      state.inviteUserLoading = PENDING;
    });
    builder.addCase(updateUser.rejected, state => {
      state.inviteUserLoading = IDLE;
    });
    builder.addCase(updateUser.fulfilled, state => {
      state.inviteUserLoading = IDLE;
    });
    builder.addCase(deleteUsers.pending, state => {
      state.deleteUsersLoading = PENDING;
    });
    builder.addCase(deleteUsers.rejected, state => {
      state.deleteUsersLoading = IDLE;
    });
    builder.addCase(deleteUsers.fulfilled, (state, {payload}) => {
      state.deleteUsersLoading = IDLE;
      console.log('payload', payload);
    });
    builder.addCase(sendReminder.pending, state => {
      state.sendReminderLoading = PENDING;
    });
    builder.addCase(sendReminder.rejected, state => {
      state.sendReminderLoading = IDLE;
    });
    builder.addCase(sendReminder.fulfilled, state => {
      state.sendReminderLoading = IDLE;
    });
  },
});

export const users = (state: RootState) => state.users.users;
export const roles = (state: RootState) => state.users.roles;
export const system_roles = (state: RootState) => state.users.system_roles;

export const {setTokens, logout} = usersSlice.actions;
export default usersSlice.reducer;
